import React, { ReactNode, useState, useRef, useCallback, useEffect } from 'react';
import { Dropdown, ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';

export interface AskAsperAccordionMenuItem {
    /** Unique key for the menu item */
    key: string;
    /** Label text to display */
    label: string;
    /** Icon to display before the label */
    icon?: ReactNode;
    /** Whether the item is disabled */
    disabled?: boolean;
    /** Whether the item is currently active/selected */
    active?: boolean;
    /** Click handler for the menu item */
    onClick?: () => void;
    /** Child menu items for submenu */
    children?: AskAsperAccordionMenuItem[];
    /** Submenu placement - 'left' or 'right' */
    submenuPlacement?: 'left' | 'right';
}

export interface AskAsperAccordionMenuProps {
    /** Menu items to display */
    items: AskAsperAccordionMenuItem[];
    /** Trigger element that opens the menu */
    children: ReactNode;
    /** Placement of the dropdown */
    placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom';
    /** Callback when menu visibility changes */
    onOpenChange?: (open: boolean) => void;
    /** Whether the menu is open (controlled) */
    open?: boolean;
    /** Data test attribute */
    'data-test'?: string;
}

// Submenu component that appears on hover
const Submenu: React.FC<{
    items: AskAsperAccordionMenuItem[];
    placement: 'left' | 'right';
}> = ({ items, placement }) => {
    return (
        <div
            className={`absolute top-0 bg-white flex flex-col items-start p-[4px] rounded-[8px] z-50 whitespace-nowrap ${
                placement === 'left' ? 'right-full mr-[4px]' : 'left-full ml-[4px]'
            }`}
            style={{
                boxShadow:
                    '0px 6px 16px 0px rgba(0,0,0,0.08), 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 9px 28px 8px rgba(0,0,0,0.05)',
            }}
            data-ask-asper-submenu
        >
            {items.map((item) => (
                <SubmenuItem key={item.key} item={item} />
            ))}
        </div>
    );
};

// Submenu item component (simpler, no nested submenus)
const SubmenuItem: React.FC<{
    item: AskAsperAccordionMenuItem;
}> = ({ item }) => {
    const handleClick = () => {
        if (!item.disabled) {
            item.onClick?.();
        }
    };

    const getTextColor = () => {
        if (item.disabled) return 'rgba(0,0,0,0.25)';
        return 'rgba(0,0,0,0.88)';
    };

    return (
        <button
            type="button"
            className={`
                flex gap-[8px] items-center h-[32px] px-[12px] py-[5px] rounded-[4px]
                border-none bg-white w-full
                hover:bg-[rgba(0,0,0,0.04)]
                ${item.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                transition-colors duration-150
            `}
            onClick={handleClick}
            disabled={item.disabled}
            aria-disabled={item.disabled}
        >
            {item.icon && (
                <span className="flex items-center justify-center shrink-0">
                    <span className="w-[16px] h-[16px] flex items-center justify-center">{item.icon}</span>
                </span>
            )}
            <span
                className="text-left whitespace-nowrap"
                style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    color: getTextColor(),
                }}
            >
                {item.label}
            </span>
        </button>
    );
};

// Helper to get color based on state
const getStateColor = (disabled?: boolean, active?: boolean, defaultColor = 'rgba(0,0,0,0.88)') => {
    if (disabled) return 'rgba(0,0,0,0.25)';
    if (active) return '#44338a';
    return defaultColor;
};

// Menu item component matching Figma design exactly
const MenuItem: React.FC<{
    item: AskAsperAccordionMenuItem;
    onClick?: () => void;
}> = ({ item, onClick }) => {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const [openedByClick, setOpenedByClick] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearCloseTimeout = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

    const handleClick = () => {
        if (item.children && item.children.length > 0) {
            clearCloseTimeout();
            const next = !showSubmenu;
            setShowSubmenu(next);
            setOpenedByClick(next);
            return;
        }
        if (!item.disabled && onClick) {
            onClick();
        }
        item.onClick?.();
    };

    const handleMouseEnter = () => {
        clearCloseTimeout();
        if (item.children && item.children.length > 0) {
            setShowSubmenu(true);
        }
    };

    const handleMouseLeave = () => {
        if (item.children && item.children.length > 0 && !openedByClick) {
            // Delay close so user can move mouse to left-positioned submenu without it disappearing
            closeTimeoutRef.current = setTimeout(() => setShowSubmenu(false), 150);
        }
    };

    const isActive = item.active || showSubmenu;

    const getBackgroundClass = () => {
        if (isActive) return 'bg-[#fbfaff]';
        return 'hover:bg-[rgba(0,0,0,0.04)]';
    };

    const textColor = getStateColor(item.disabled, isActive);
    const iconColor = getStateColor(item.disabled, isActive);

    const hasSubmenu = item.children && item.children.length > 0;

    return (
        <div
            ref={itemRef}
            className="relative w-full"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                type="button"
                className={`
                    flex gap-[8px] items-center w-full h-[32px] px-[12px] py-[5px] rounded-[4px]
                    border-none bg-transparent
                    ${getBackgroundClass()}
                    ${item.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    transition-colors duration-150
                `}
                onClick={handleClick}
                disabled={item.disabled}
                aria-disabled={item.disabled}
                aria-haspopup={hasSubmenu ? 'menu' : undefined}
                aria-expanded={hasSubmenu ? showSubmenu : undefined}
            >
                {item.icon && (
                    <span
                        className="flex items-center justify-center w-[24px] h-[24px] shrink-0"
                        style={{ color: iconColor }}
                    >
                        {item.icon}
                    </span>
                )}
                <span
                    className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '22px',
                        color: textColor,
                    }}
                >
                    {item.label}
                </span>
                {/* Chevron for submenu */}
                {hasSubmenu && (
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={item.submenuPlacement === 'left' ? 'rotate-180' : ''}
                        style={{ color: iconColor }}
                    >
                        <path
                            d="M9 18L15 12L9 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </button>
            {/* Submenu */}
            {hasSubmenu && showSubmenu && (
                <Submenu items={item.children!} placement={item.submenuPlacement || 'left'} />
            )}
        </div>
    );
};

// Menu content component extracted for better performance
const MenuContent: React.FC<{
    items: AskAsperAccordionMenuItem[];
    dataTest?: string;
}> = ({ items, dataTest }) => (
    <div
        className="bg-white flex flex-col items-start p-1 rounded-lg min-w-[160px]"
        style={{
            boxShadow:
                '0px 6px 16px 0px rgba(0,0,0,0.08), 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 9px 28px 8px rgba(0,0,0,0.05)',
        }}
        data-test={dataTest}
        data-ask-asper-accordion-menu
    >
        {items.map((item) => (
            <MenuItem key={item.key} item={item} />
        ))}
    </div>
);

const AskAsperAccordionMenu: React.FC<AskAsperAccordionMenuProps> = (props) => {
    const { items, children, placement = 'bottomRight', onOpenChange, open } = props;

    const theme = useTheme();

    return (
        <ConfigProvider theme={theme}>
            <Dropdown
                dropdownRender={() => <MenuContent items={items} dataTest={props['data-test']} />}
                trigger={['click']}
                placement={placement}
                onOpenChange={onOpenChange}
                open={open}
            >
                {children}
            </Dropdown>
        </ConfigProvider>
    );
};

export default AskAsperAccordionMenu;
